import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, keyframes } from "@mui/material";
import { styled } from "@mui/material/styles";

// Bouncing animation for dots
const bounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
`;

// Styled dot component with animation
// Use shouldForwardProp to prevent 'delay' from being forwarded to the DOM
const TypingDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "delay",
})(({ theme, delay }) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: theme.palette.primary.main,
  animation: `${bounce} 1.4s infinite ease-in-out both`,
  animationDelay: `${delay}s`,
}));

// Container for the dots
const DotsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  padding: theme.spacing(1.5),
  borderRadius: "12px",
  backgroundColor: theme.palette.grey[100],
  maxWidth: "60px",
}));

// Main container with optional status text
const TypingContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 4,
  alignItems: "flex-start",
});

/**
 * Typing indicator component that shows animated dots and optional status message.
 * Used to indicate Riff is processing/thinking.
 *
 * @param {Object} props
 * @param {string} props.status - Optional status message to display below the dots
 */
const TypingIndicator = ({ status = null }) => {
  return (
    <TypingContainer>
      <DotsContainer>
        <TypingDot delay={-0.32} />
        <TypingDot delay={-0.16} />
        <TypingDot delay={0} />
      </DotsContainer>
      {status && (
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontStyle: "italic",
            ml: 1,
            fontSize: "0.75rem",
          }}
        >
          {status}
        </Typography>
      )}
    </TypingContainer>
  );
};

TypingIndicator.propTypes = {
  /** Optional status message to display below the dots */
  status: PropTypes.string,
};

TypingIndicator.defaultProps = {
  status: null,
};

export default TypingIndicator;
